import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { Widget } from '@ispm/widgets/decorator/widget.decorator';
import { IWidgetItem } from '@ispm/widgets/interfaces/widget-item.interface';
import { get, findIndex, isEmpty, isObject, cloneDeep, set, sortBy, merge, isEqual, find, omit } from 'lodash';
import { IAuthorizationWidget, ISourceProperty } from './interfaces';
import { Authorization } from './classes';
import { AuthorizationComposerValidator } from './classes/authorization-composer-validator';
import { AgregationService } from '@ispm-client/modules/aggregator/services/aggregator.service';
import { FormGroup } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { IdynamicFormItem } from '@ispm/dynamic-form/interfaces/IdynamicFormItem';

let AUTHORIZATION_GREY_OUT_KEY: string;
let AUTHORIZATION_DISABLE_KEY: ISourceProperty;

@Component({
  selector: 'ispm-authorization-composer',
  templateUrl: './authorization-composer.component.html',
  styleUrls: ['./authorization-composer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

@Widget('authorizationComposer')
export class AuthorizationComposerComponent implements OnInit, IWidgetItem, OnDestroy {

  constructor(
    public _aggregatorService: AgregationService,
    private _changeDetector: ChangeDetectorRef
  ) {
  }

  set widgetConfig(widgetConfig: any) {
    this.config = widgetConfig.widgetPayload;
    this.formItemConfig = widgetConfig;
    AUTHORIZATION_GREY_OUT_KEY = get(this.config, 'greyOutBasedOnProperty');
    AUTHORIZATION_DISABLE_KEY =  get(this.config, 'disableItemBasedOnProperty');
  }

  get widgetFormInstance() {
    return this._widgetFormInstance;
  }

  set widgetData(widgetData: any) {
    this.authorization = [];
    if (!isEmpty(widgetData)
      && !isEqual(omit(widgetData, ['__proto__']), omit(this.authorization, ['__proto__']))) {
      this.authorization = widgetData;
      if (!isEmpty(this.data)) {
        this.processAttachLogic(this.data);
      }
    }
  }

  set widgetDisabledStatus(value) {
    this._widgetDisabledStatus = value;
  }

  get widgetDisabledStatus() {
    return this._widgetDisabledStatus;
  }

  set widgetFormInstance(widgetFormInstance: FormGroup) {
    if (this.config && !isEqual(this._widgetFormInstance, widgetFormInstance)) {
      this._widgetFormInstance = widgetFormInstance;
      this.handleWidgetFormInstance(widgetFormInstance);
    }
  }

  get valid() {
    return this._valid;
  }

  set valid(valid: boolean) {
    if (this.formItemConfig && this.formItemConfig.required) {
      this._valid = valid;
    } else {
      this._valid = true;
    }
    const asyncValidate = new AuthorizationComposerValidator(() => this._valid);
    if (this._widgetFormInstance.controls[get(this.formItemConfig, 'name')]) {
      this._widgetFormInstance.controls[get(this.formItemConfig, 'name')]
        .setAsyncValidators([asyncValidate.validate.bind(asyncValidate)]);
    }
  }

  public config: IAuthorizationWidget;
  public data: any[] = [];
  public dataBackup: any[] = [];
  private authorization: Authorization[] = [];
  public widgetUpdateValueCallback: (value: any) => void;
  public _widgetDisabledStatus: boolean;
  public _widgetFormInstance: FormGroup;
  public loading: boolean;
  private destroyed$: Subject<any> = new Subject();
  private _valid: boolean = true;
  private formItemConfig: IdynamicFormItem;
  private controlValue;

  /**
   * @method getValueFromItem
   * @description helps to compare the value when emitted model is inconsistent
   * @param item - Emitted Object or String from form item
   */
  private static getValueFromItem(item: any): any {
    if (isObject(item) && item['value']) {
      return item['value'];
    } else if (!isObject(item) && item) {
      return item;
    } else {
      return null;
    }
  }

  widgetValidator(): boolean {
    return this._valid;
  }

  ngOnInit() {
  }

  /**
   * @method handleWidgetFormInstance
   * @description Handle the logic when widgetFormInstance is set
   * @param widgetFormInstance
   */
  handleWidgetFormInstance(widgetFormInstance: FormGroup): void {
    if (this.config.listenToFieldFromParent && get(this._widgetFormInstance.controls, `${this.config.listenToFieldFromParent}`)) {

      /** Check the initial value and fetch data based on that */
      if (!isEqual(get(widgetFormInstance.value, this.config.listenToFieldFromParent), this.controlValue)) {
        this.loading = true;
        this.triggerChangeDetector();
        this.handleItemUpdate(get(widgetFormInstance.value, this.config.listenToFieldFromParent), widgetFormInstance.value)
          .takeUntil(this.destroyed$.asObservable())
          .subscribe(data => this.processListData(data));
      }

      /** subscribe to the parent form item and fetch new data on update */
      // todo: currently listening to one item, the logic can be enhanced to support form change / group of items change as a stream
      this._widgetFormInstance.controls[this.config.listenToFieldFromParent].valueChanges
        .map(value => AuthorizationComposerComponent.getValueFromItem(value))
        .filter(value => !isEmpty(value) && !isEqual(value, this.controlValue))
        .switchMap((item: any) => {
          this.loading = true;
          this.triggerChangeDetector();
          return this.handleItemUpdate(item, widgetFormInstance.value);
        })
        .takeUntil(this.destroyed$.asObservable())
        .subscribe(data => this.processListData(data));

    } else if (this.config.dataBinding && this.config.dataBinding.length) {

      this.loading = true;
      this.triggerChangeDetector();
      /** If no parent form dependency, fetch directly using aggregator */
      this._aggregatorService.registerQuery(this.config.dataBinding)
        .take(1)
        .subscribe(data => this.processListData(data));

    } else {
      /**  Do Nothing */
    }
  }

  /**
   * @method handleItemUpdate
   * @description Handle the new data fetch on every ite change event
   * @access private
   * @param item
   * @param formValue
   */
  private handleItemUpdate(item, formValue: any): Observable<any> {
    if (this.config.dataBinding && this.config.dataBinding.length > 0 && item) {
      this.controlValue = AuthorizationComposerComponent.getValueFromItem(item);
      const tempData = merge(cloneDeep(formValue), set({ }, this.config.listenToFieldFromParent, this.controlValue));
      const dataBindingsList = cloneDeep(this.config.dataBinding);
      dataBindingsList.forEach(db => {
        db.queryString = this._aggregatorService.interpolate(db.queryString, tempData);
        return db;
      });
      return this._aggregatorService.registerQuery(dataBindingsList).take(1);
    } else {
      return Observable.of([]);
    }
  }


  /**
   * @method processListData
   * @description process the data and predict selection based on authorization
   * @access private
   * @param data
   */
  private processListData(data: any): void {
    const index = findIndex(data, d => d.hasOwnProperty(this.config.tableDataKey));
    if (index > -1 && !isEmpty(get(data, `${index}.${this.config.tableDataKey}`))) {
      this.processAttachLogic(get(data, `${index}.${this.config.tableDataKey}`, []));
    } else {
      this.processAttachLogic([]);
    }
  }

  /**
   *
   * @Method onDataUpdate
   * @param tableData - updated table data (data list)
   * @description It is used to listen to table data change
   * On every update of this form, re-process the authorization to append or remove the references
   * @access public this method is used in HTML template, hence provisioning as public
   */
  public onDataUpdate(tableData: any[]): void {
    if (tableData.some(data => data[this.config.selectedRowIdentifier] === undefined)) {
      tableData = tableData.map(data => {
        if (data[this.config.selectedRowIdentifier] === undefined) {
          data[this.config.selectedRowIdentifier] = false;
        }
        return data;
      });
    }
    if (!isEmpty(tableData) && !isEqual(this.dataBackup, tableData)) {
      this.processDetachLogic(tableData);
    }
  }

  /**
   * @method processAttachLogic
   * @description It is used to process the initial logic when widget data
   *              is set and to do the transformation of table data based on the value
   * @param dataArray - table data (data list) to render the dynamic form table
   * @access private
   */
  private processAttachLogic(dataArray: any[]): void {
    if (this.config.sortTableAttribute && dataArray.length) {
      dataArray = sortBy(dataArray, (item) => {
        return get(item, `${this.config.sortTableAttribute}`, '').toLowerCase();
      });
    }
    dataArray = dataArray.map(data => {
      const item = find(this.authorization, { reference : data.id });
      data[this.config.selectedRowIdentifier] = !!item;
      if (!isEmpty(AUTHORIZATION_DISABLE_KEY) && !isEmpty(get(item, `${AUTHORIZATION_DISABLE_KEY.key}`))) {
        const attribute = get(item, `${AUTHORIZATION_DISABLE_KEY.key}`);
        if (get(attribute, `${AUTHORIZATION_DISABLE_KEY.path}`) === get(AUTHORIZATION_DISABLE_KEY, 'value')) {
          data[AUTHORIZATION_DISABLE_KEY.key] = item[AUTHORIZATION_DISABLE_KEY.key];
        }
      }
      return data;
    });
    this.data = dataArray;
    this.valid = dataArray.length === 0 || dataArray.some(data => data[this.config.selectedRowIdentifier]);
    this.loading = false;
    this.triggerChangeDetector();
  }

  /**
   * @method processDetachLogic
   * @description It is used to process the initial logic when widget data
   *              is set and to do the transformation of table data based on the value
   * @param tableData table data from updating dynamic form table
   * @access private
   */
  private processDetachLogic(tableData: any[]): void {
    for (const data of tableData) {
      const index = findIndex(this.authorization, { reference: data.id });
      if (get(data, this.config.selectedRowIdentifier) && index === -1) {
        this.authorization.push(
          new Authorization(
            get(data, `${this.config.model.reference || 'id'}`),
            this.config.model.resourceType,
            this.config.model.subResourceType
          )
        );
      } else if (!(get(data, this.config.selectedRowIdentifier)) && index > -1) {
        this.authorization.splice(index, 1);
      } else {
        /**  Do Nothing */
      }
    }
    this.dataBackup = cloneDeep(tableData);
    this.valid = tableData.some(data => data[this.config.selectedRowIdentifier]);
    this.widgetUpdateValueCallback.apply(this, [this.authorization]);
  }

  /**
   * @Method getEditMode
   * @description reference method to handle edit mode of the form item references
   * @access public this method is used in HTML template, hence provisioning as public
   */
  public getEditMode(): boolean {
    return get(this.config, 'isEditable', true);
  }

  /**
   * @Method checkIfGreyOut
   * @description reference method to handle grey mode of the table row reference and attached the CSS class "auth-show-as-grey"
   * @access public this method is used in HTML template, hence provisioning as public
   */
  public checkIfGreyOut(item: any) {
    if (AUTHORIZATION_GREY_OUT_KEY) {
      if (item && item.hasOwnProperty(AUTHORIZATION_GREY_OUT_KEY)
        && (item[AUTHORIZATION_GREY_OUT_KEY] == true
          || !isEmpty(item[AUTHORIZATION_GREY_OUT_KEY]))) {
        return { };
      }
      return { 'auth-show-as-grey': true };
    }
    return { };
  }

  /**
   * @method triggerChangeDetector
   * @description common method to avoid duplicate code blocks on detecting changes
   * @access private
   */
  private triggerChangeDetector() {
    this._changeDetector.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /**
   * @method isItemDisable
   * @description reference method to disable the table row
   * @param item
   */
  public isItemDisable(item: any) {
    if (!isEmpty(AUTHORIZATION_DISABLE_KEY) && !isEmpty(get(item, `${AUTHORIZATION_DISABLE_KEY.key}`))) {
      const value = get(item, `${AUTHORIZATION_DISABLE_KEY.key}.${AUTHORIZATION_DISABLE_KEY.path}`);
      if ( value === get(AUTHORIZATION_DISABLE_KEY, 'value')) {
        return true;
      }
    }
  }
}
